import { Field as IField, FieldType, HeadingLevel } from "@/core/types/types";
import Field from "@/core/components/Field";
import { Theme } from "@mui/system";
import { Box, Button, Divider, Typography } from "@mui/material";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import { useFormContext } from "react-hook-form";
import { useGetPreviousAnswers } from "@/core/hooks/useGetPreviousAnswers/useGetPreviousAnswers";
import React, { useEffect, useState } from "react";
import { TransitionGroup } from "react-transition-group";
import { generateUUID } from "@datadog/browser-core";
import FadeCollapse from "@/components/custom/AdditionalPassenger/FadeCollapse";
import useCustomStyles from "@/core/hooks/useCustomStyles/useCustomStyles";

interface PassengerAdditionProps {
  fields: IField[];
}

interface Passenger {
  id: string;
  [key: string]: any;
}

const styles = (theme: Theme) => ({
  radioGroup: {
    marginBottom: theme.spacing(1),
    "& .MuiFormGroup-root": {
      display: "flex",
      flexDirection: "row",
      flexWrap: "nowrap",
      gap: theme.spacing(1),
    },
    "& .MuiFormControlLabel-root": {
      paddingRight: theme.spacing(1),
      borderColor: theme.palette.grey.A700,
      "&:has(.Mui-checked)": {
        borderColor: theme.palette.primary.main,
      },
    },
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.grey[800],
      },
    },
  },
  checkbox: {
    "& .MuiButtonBase-root": {
      border: "none",
    },
  },
  formContainer: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
  },
});

const getFieldStyles = (classes: any, fieldType: string) => {
  switch (fieldType) {
    case FieldType.Radio:
      return classes.radioGroup;
    case FieldType.TextField:
      return classes.textField;
    case FieldType.Checkbox:
      return classes.checkbox;
    default:
      return {};
  }
};

const PASSENGERS_FIELD: string = "passengers";
const MAX_NUMBER_OF_PASSENGERS: number = 20;

const AdditionalPassenger = ({ fields }: PassengerAdditionProps) => {
  const { setValue, watch } = useFormContext();
  const passengersFormValues = watch(PASSENGERS_FIELD);

  const requiredInputVariables = fields
    .map((field) => field.properties.requiredInputVariables)
    .filter((requiredInputVariables) => requiredInputVariables !== undefined)
    .join(",");
  const { result } = useGetPreviousAnswers(requiredInputVariables, {
    enableCaching: false,
  });
  const { numberOfPassengers } = result;

  const classes: any = useCustomStyles(styles);

  const [passengers, setPassengers] = useState<Passenger[]>([]);

  useEffect(() => {
    if (passengersFormValues) {
      setPassengers(passengersFormValues);
    }
  }, [passengersFormValues]);

  useEffect(() => {
    if (numberOfPassengers && passengers.length === 0) {
      appendPassengers(numberOfPassengers - 1);
    }
  }, [numberOfPassengers]);

  const handleAdd = () => {
    if (passengers.length < MAX_NUMBER_OF_PASSENGERS) {
      appendPassengers(1);
    }
  };

  const appendPassengers = (amount: number) => {
    const newPassengers: Passenger[] = [...Array(amount)].map((_) => {
      return { id: generateUUID(), isUnderage: false };
    });
    const updatedPassengers = [...passengers].concat(newPassengers);

    setValue(PASSENGERS_FIELD, updatedPassengers);
  };

  const handleRemove = (index: number) => {
    const updatedPassengers = passengers.filter((_, i) => i !== index);
    setValue(PASSENGERS_FIELD, updatedPassengers);
  };

  return (
    <>
      {numberOfPassengers !== 0 && (
        <Box my={2}>
          <Divider />
        </Box>
      )}
      <Box id={PASSENGERS_FIELD}>
        <TransitionGroup>
          {passengers.map((passenger: Passenger, index: number) => {
            return (
              <FadeCollapse key={passenger.id || index} timeout={400}>
                <Box>
                  <Box sx={classes?.formContainer}>
                    <Box mb={2}>
                      <Button
                        onClick={() => handleRemove(index)}
                        color="inherit"
                        sx={{ padding: 0 }}
                      >
                        <RemoveCircleIcon />
                        <Box ml={1}>
                          <Typography
                            sx={{ textTransform: "none" }}
                            variant={HeadingLevel.H3}
                          >
                            {`${t("additionalPassenger.traveler")} ${
                              index + 2
                            }`}
                          </Typography>
                        </Box>
                      </Button>
                    </Box>
                    {fields.map((field: IField) => {
                      const fieldStyles = getFieldStyles(classes, field.type);
                      const uniqueFieldKey = `passengers[${index}].${field.key}`;
                      const defaultValue = passenger[field.key];
                      const required = field.properties.required;

                      return (
                        <Box key={uniqueFieldKey} sx={fieldStyles}>
                          <Field
                            field={{
                              ...field,
                              key: uniqueFieldKey,
                              defaultValue,
                              validate: { ...field.validate, required },
                              properties: {
                                ...field.properties,
                                errorTranslationKey: field.key,
                              },
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                  {index !== MAX_NUMBER_OF_PASSENGERS - 1 && (
                    <Box my={2}>
                      <Divider />
                    </Box>
                  )}
                </Box>
              </FadeCollapse>
            );
          })}
        </TransitionGroup>
        <Box mb={4}>
          {passengers.length < MAX_NUMBER_OF_PASSENGERS && (
            <Button onClick={handleAdd} color="inherit" sx={{ padding: 0 }}>
              <AddCircleIcon />
              <Box ml={1}>
                <Typography
                  sx={{ textTransform: "none" }}
                  variant={HeadingLevel.H3}
                >
                  {t("additionalPassenger.addTraveler")}
                </Typography>
              </Box>
            </Button>
          )}
        </Box>
      </Box>
    </>
  );
};

export default AdditionalPassenger;
